import React, {useCallback, useEffect, useState, useMemo} from 'react';
import {useParams} from 'react-router-dom';
import axios from 'axios';
import {Box, Button, Card, CardContent, Grid, Collapse, Typography, IconButton} from '@mui/material';
import Cookies from "js-cookie";
import {format, parseISO} from "date-fns";
import {DataGrid} from "@mui/x-data-grid";
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import SnoozeIcon from '@mui/icons-material/Snooze';
import CloseIcon from '@mui/icons-material/Close';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import ManageOccupancy from './ManageOccupancy';
import GroupForm from "./GroupForm";
import ManageQueuePanel from "./ManageQueuePanel";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const ManageQueue = (callback, deps) => {
    const {queueId} = useParams();
    const [queueData, setQueueData] = useState(null);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [occupancyLoading, setOccupancyLoading] = useState(false);
    const [groupLoading, setGroupLoading] = useState(false);
    const [isClosingBatch, setIsClosingBatch] = useState(false);


    const [isGroupFormOpen, setIsGroupFormOpen] = useState(false);
    const [isLeftPanelOpen, setIsLeftPanelOpen] = useState(false);

    const formatDate = (dateString) => {
        try {
            return format(parseISO(dateString), 'h:mm:ssa');
        } catch {
            return dateString;
        }
    };


    const columns = [
        {field: 'batchName', headerName: 'Group', width: 100, flex: 0.5 },
        { field: 'position', headerName: '#', width: 100, flex: 0.5 },
        { field: 'name', headerName: 'Name', width: 100, flex: 1 },
        { field: 'size', headerName: 'Size', width: 90, flex: 0.7},
        { field: 'code', headerName: 'Code', width: 80, flex: 1 },
        { field: 'timestampJoin', headerName: 'Joined', width: 180, flex: 1, valueFormatter: (params) => formatDate(params.value)},
        { field: 'type', headerName: 'Type', width: 80, flex: 0.8 },
        {
            field: 'status',
            headerName: 'Status',
            width: 100,
            flex: 0.8
        },
        {
            field: 'remove',
            headerName: 'X',
            sortable: false,
            renderCell: (params) => (
                <Button
                    variant="contained"
                    sx={{height: '25px', minWidth: 'fit-content', paddingInline: '4px'}}
                    color="error"
                    onClick={() => handleRemove(params.row.groupId)}
                    disabled={groupLoading}
                >
                    <CloseIcon/>
                </Button>
            ),
            width: 50,
            flex: 0.5
        },
        {
            field: 'setback',
            headerName: 'No Show',
            sortable: false,
            renderCell: (params) => (
                <Button
                    variant="contained"
                    sx={{height: '25px'}}
                    color="secondary"
                    onClick={() => handleNoShow(params.row.groupId, params.row.setback)}
                    disabled={groupLoading || params.row.status === 'NOSHOW'}
                >
                    <SnoozeIcon/>
                </Button>
            ),
            width: 100
        },
        {
            field: 'notify',
            headerName: 'Notify',
            sortable: false,
            renderCell: (params) => (
                <Button
                    variant="contained"
                    sx={{height: '25px'}}
                    color="warning"
                    onClick={() => handleNotify(params.row.batchName)}
                    disabled={groupLoading || params.row.status === 'NOTIFIED'}
                >
                    <NotificationImportantIcon/>
                </Button>
            ),
            width: 100
        },
        {
            field: 'entered',
            headerName: 'Entered',
            sortable: false,
            renderCell: (params) => (
                <Button
                    variant="contained"
                    sx={{height: '25px'}}
                    color="success"
                    onClick={() => handleEnter(params.row.groupId)}
                    disabled={groupLoading}
                >
                    <KeyboardDoubleArrowRightIcon/>
                </Button>
            ),
            width: 100
        },
    ];

    const fetchQueueData = useCallback(async () => {
        try {
            const csrfToken = Cookies.get('XSRF-TOKEN');
            const response = await axios.get(`/api/queues/staff/${queueId}`, {
                headers: {
                    'X-CSRF-TOKEN': csrfToken
                }
            });
            setQueueData(response.data);
        } catch (error) {
            console.error('Error fetching queue data', error);
        }
    }, [queueId]);

    const fetchGroupData = useCallback(async () => {
        try {
            const csrfToken = Cookies.get('XSRF-TOKEN');
            const response = await axios.get(`/api/groups/staff/inline`, {
                headers: {
                    'X-CSRF-TOKEN': csrfToken
                }
            });
            setGroups(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching queue data', error);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (queueId) {
            fetchQueueData();
            fetchGroupData();
        }
    }, [queueId, fetchQueueData, fetchGroupData]);

    useEffect(() => {
        let fetchQueueInterval;
        let fetchGroupInterval;
        if (queueId) {
            fetchQueueInterval = setInterval(fetchQueueData, 3000);
            fetchGroupInterval = setInterval(fetchGroupData, 3000);
        }

        // Clean-up
        return () => {
            clearInterval(fetchQueueInterval);
            clearInterval(fetchGroupInterval);
        };
    }, [queueId, fetchQueueData, fetchGroupData]);


    const updateGroup = async (groupId, data) => {
        const csrfToken = Cookies.get('XSRF-TOKEN');
        const headers = { 'X-CSRF-TOKEN': csrfToken };
        setGroupLoading(true);
        try {
            if (groupId && data) {
                // Update existing group
                await axios.put(`/api/groups/staff/${groupId}`, data, { headers });
            }
            fetchQueueData(); // Refresh the data
            fetchGroupData();
        } catch (error) {
            console.error('Error saving group', error);
            // Handle errors (e.g., show an error message)
        } finally {
            setGroupLoading(false);
        }
    };

    const updateOccupancy = async (number) => {
        const csrfToken = Cookies.get('XSRF-TOKEN');
        const headers = { 'X-CSRF-TOKEN': csrfToken };
        setOccupancyLoading(true);
        try {
            const data = new URLSearchParams({ change: number });
            if (queueId) {
                await axios.put(`/api/queues/staff/${queueId}/occupancy`, data, { headers });
            }
            fetchQueueData(); // Refresh the data
            fetchGroupData();
        } catch (error) {
            console.error('Error saving queue occupancy', error);
            // Handle errors (e.g., show an error message)
        } finally {
            setOccupancyLoading(false);
        }
    };

    const closeBatch = async () => {
        // hard lock: if already in-flight, do nothing
        if (isClosingBatch) return;

        const csrfToken = Cookies.get('XSRF-TOKEN');
        const headers = { 'X-CSRF-TOKEN': csrfToken };

        setIsClosingBatch(true);
        setGroupLoading(true);

        try {
            if (queueId) {
                // axios.post(url, data, config). No body needed here, so send null.
                await axios.post(`/api/queues/staff/${queueId}/closeCurrentBatch`, null, { headers });
            }

            // refresh
            fetchQueueData();
            fetchGroupData();
        } catch (error) {
            console.error('Error closing batch', error);
        } finally {
            setGroupLoading(false);
            setTimeout(() => setIsClosingBatch(false), 1000);
        }
    };


    const notifyBatch = async (batchName) => {
        const csrfToken = Cookies.get('XSRF-TOKEN');
        const headers = { 'X-CSRF-TOKEN': csrfToken };
        setGroupLoading(true);
        try {
            const data = new URLSearchParams({ batchName: batchName });
            if (queueId) {
                await axios.put(`/api/groups/staff/${queueId}/notifyBatch`, data, { headers });
            }
            fetchQueueData(); // Refresh the data
            fetchGroupData();
        } catch (error) {
            console.error('Error saving queue occupancy', error);
            // Handle errors (e.g., show an error message)
        } finally {
            setGroupLoading(false);
        }
    };

    const handleRemove = (groupId) => {
        const newData = { status: 'CANCELLED' };
        updateGroup(groupId, newData);
    };

    const handleNoShow = (groupId, currentSetback) => {
        const newData = { setback: queueData?.noShowSetBack + currentSetback, status: 'NOSHOW'};
        updateGroup(groupId, newData);
    };

    const handleNotify = (batchName) => {
        notifyBatch(batchName);
    };

    const handleEnter = (groupId) => {
        const newData = { status: 'INSIDE' };
        updateGroup(groupId, newData);
    };

    // Function to change visitors count
    const changeVisitors = (number) => {
        console.log("Changing visitors by: ", number);
        updateOccupancy(number);

    };

    // Determine the color for occupancy display
    const occupancyColor = queueData?.currentCapacity > queueData?.capacity ? 'red' : queueData?.currentCapacity === queueData?.capacity ? 'white' : 'lightGreen';
    const handleAdd = () => {
        setIsGroupFormOpen(true);
    };

    const handleCloseBatch = () => {
        closeBatch();
    };


    const toggleLeftPanel = () => {
        setIsLeftPanelOpen(!isLeftPanelOpen);
    };

    return (
        <>
            {queueData ?
                <>
                    <GroupForm
                        open={isGroupFormOpen}
                        onClose={() => setIsGroupFormOpen(false)}
                        onSave={fetchGroupData}
                        queueId={queueId}
                    />
                <Grid container spacing={2}
                               style={{height: '90vh', width: '100%', paddingTop: '20px', paddingInline: '5px'}}>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: 0}}>
                        <Typography align="center" sx={{fontSize: '1.3rem', fontWeight: 600, padding: '5px 20px'}}>
                            Manage Queue #{queueId}
                        </Typography>
                        <Button variant="contained" color="success" onClick={handleAdd} sx={{height: '25px'}}>
                            + Add People
                        </Button>
                        <Button
                            variant="contained"
                            color="warning"
                            onClick={handleCloseBatch}
                            disabled={groupLoading || isClosingBatch}
                            sx={{height: '25px', ml: '10px'}}
                        >
                            {isClosingBatch ? 'Closing…' : 'Close Group'}
                        </Button>
                    </Box>
                    {/* Top Panel */}
                    <Grid item xs={12} style={{height: '65%'}}>
                        <Card raised style={{height: '100%'}}>
                            <CardContent style={{ height: '100%', overflow: "auto" }}>
                                <DataGrid
                                    density="compact"
                                    rows={groups}
                                    columns={columns}
                                    pageSize={10}
                                    loading={loading}
                                    rowsPerPageOptions={[10]}
                                    getRowId={(row) => row.groupId}
                                />
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Bottom Panels */}
                    <Grid item container xs={12} spacing={2} style={{height: '34%'}}>
                        {/* Left Panel */}
                        <Grid item xs={isLeftPanelOpen ? 5 : 1} style={{ height: '100%' }}>
                            <Card raised style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <CardContent style={{ height: '100%', overflowY: 'auto', flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                        <Typography variant={isLeftPanelOpen ? 'h6' : 'body'} gutterBottom>Manage Queue</Typography>
                                        <Button
                                            sx={{}}
                                            variant="outlined"
                                            color="primary"
                                            onClick={toggleLeftPanel}
                                            size="small"
                                            startIcon={isLeftPanelOpen ? <ArrowBackIcon /> : <ArrowForwardIcon />}
                                        >
                                            {isLeftPanelOpen ? 'Close' : ''}
                                        </Button>
                                    </Box>
                                    <Collapse in={isLeftPanelOpen} timeout="auto" unmountOnExit>
                                        <ManageQueuePanel queueData={queueData} />
                                    </Collapse>
                                </CardContent>
                            </Card>
                        </Grid>
                        {/* Right Panel*/}
                        <Grid item xs={isLeftPanelOpen ? 7 : 11} style={{height: '100%'}}>
                            <Card raised style={{height: '100%'}}>
                                <CardContent style={{ height: '100%', overflowY: 'auto' }}>
                                    <ManageOccupancy queueData={queueData}
                                                     changeVisitors={changeVisitors}
                                                     loading={occupancyLoading}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Grid>
                </> :
                <Typography align="center" sx={{fontSize: '1.3rem', fontWeight: 600, padding: '5px 20px'}}>
                    Queue could not be loaded.
                </Typography>}
        </>

    );
};

export default ManageQueue;
